<script lang="ts">
  import type { ActivityStep } from "../../db/types";
  import { i18n } from "../../i18n/i18n.svelte";

  // Journal d'activité d'une réponse : reformulation, recherches (avec la
  // requête exacte envoyée) et réflexion du modèle, dans l'ordre. Ouvert en
  // direct pendant la génération, replié sous la réponse une fois terminée.
  interface Props {
    steps: ActivityStep[];
    open?: boolean;
    live?: boolean;
  }

  let { steps, open = false, live = false }: Props = $props();

  const fr = {
    sourceLabel: { local: "Documents", epfl: "EPFL", polylex: "LEX", web: "Web" },
    sourceUnit: { local: "passage", epfl: "résultat", polylex: "résultat", web: "résultat" },
    activity: "Activité de l'assistant",
    searches: (n: number) => `${n} recherche${n > 1 ? "s" : ""}`,
    thinking: "réflexion",
    failed: "échec",
    rewrite: "Reformulation",
    read: "Lecture",
    page: "Page web",
    pageRead: "lue",
    tool: "Outil",
    thinkingLabel: "Réflexion",
    quoted: (s: string) => `« ${s} »`,
  };
  const en: typeof fr = {
    sourceLabel: { local: "Documents", epfl: "EPFL", polylex: "LEX", web: "Web" },
    sourceUnit: { local: "passage", epfl: "result", polylex: "result", web: "result" },
    activity: "Assistant activity",
    searches: (n) => `${n} search${n > 1 ? "es" : ""}`,
    thinking: "thinking",
    failed: "failed",
    rewrite: "Rephrasing",
    read: "Reading",
    page: "Web page",
    pageRead: "read",
    tool: "Tool",
    thinkingLabel: "Thinking",
    quoted: (s) => `“${s}”`,
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  const searchCount = $derived(
    steps.filter((s) => s.kind === "search" || s.kind === "read" || s.kind === "page").length,
  );
  const hasThinking = $derived(steps.some((s) => s.kind === "thinking"));

  const summary = $derived.by(() => {
    if (live) return `${t.activity}…`;
    const parts: string[] = [];
    if (searchCount > 0) parts.push(t.searches(searchCount));
    if (hasThinking) parts.push(t.thinking);
    return `${t.activity}${parts.length ? ` (${parts.join(", ")})` : ""}`;
  });

  function countLabel(step: Extract<ActivityStep, { kind: "search" | "read" }>): string {
    if (step.kind === "search" && step.message) return step.message;
    if (step.failed) return t.failed;
    if (step.count === undefined) return "…";
    const unit = step.kind === "read" ? t.sourceUnit.local : t.sourceUnit[step.source];
    return `${step.count} ${unit}${step.count > 1 ? "s" : ""}`;
  }
</script>

<details class="agent-activity" {open}>
  <summary>{summary}</summary>
  <ol>
    {#each steps as step, i (i)}
      <li class={step.kind}>
        {#if step.kind === "rewrite"}
          <span class="label">{t.rewrite}</span>
          <span class="query">{t.quoted(step.query)}</span>
        {:else if step.kind === "search"}
          <span class="label">{t.sourceLabel[step.source]}</span>
          <span class="query">{t.quoted(step.query)}</span>
          <span class="count" class:failed={step.failed}>{countLabel(step)}</span>
        {:else if step.kind === "read"}
          <span class="label">{t.read}</span>
          <span class="query">{step.doc}</span>
          <span class="count" class:failed={step.failed}>{countLabel(step)}</span>
        {:else if step.kind === "page"}
          <span class="label">{t.page}</span>
          <span class="query" title={step.url}>{step.title ?? step.url}</span>
          <span class="count" class:failed={step.failed}>{step.failed ? t.failed : step.title ? t.pageRead : "…"}</span>
        {:else if step.kind === "tool"}
          <span class="label">{t.tool}</span>
          <span class="query">{step.name}</span>
        {:else if step.kind === "notice"}
          <span class="notice">{step.text}</span>
        {:else if step.kind === "directory"}
          <span class="notice">EPFL : {step.query} → {step.result ?? "…"}</span>
        {:else}
          <span class="label">{t.thinkingLabel}</span>
          <pre>{step.text}</pre>
        {/if}
      </li>
    {/each}
  </ol>
</details>

<style>
  .agent-activity {
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.4rem 0.6rem;
    font-size: 0.78rem;
    color: var(--epfl-gray-600);
  }

  summary {
    cursor: pointer;
    color: var(--epfl-gray-400);
    font-style: italic;
    user-select: none;
  }

  ol {
    list-style: none;
    margin: 0.4rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35rem;
    line-height: 1.35;
  }

  .label {
    flex-shrink: 0;
    padding: 0.05rem 0.4rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--epfl-gray-600);
  }

  .query {
    color: var(--epfl-text);
    word-break: break-word;
  }

  .count {
    color: var(--epfl-gray-400);
    white-space: nowrap;
  }

  .count.failed {
    color: var(--epfl-danger-text);
  }

  .notice {
    color: var(--epfl-warning-text);
    font-style: italic;
  }

  li.thinking {
    flex-direction: column;
    align-items: flex-start;
  }

  pre {
    margin: 0;
    width: 100%;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    color: var(--epfl-gray-600);
    max-height: 220px;
    overflow-y: auto;
  }
</style>
